import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as Icons from 'react-icons/fa';
import ChatBot from './components/ChatBot';
import ProjectCard from './components/ProjectCard';
import ContactForm from './components/ContactForm';
import LanguagesSection from './components/LanguagesSection';
import AccordionCard from './components/AccordionCard';
import { useLanguage } from './contexts/LanguageContext';
import { fetchGitHubRepos, getTechStack, GitHubRepo } from './services/githubService';

const {
  FaGithub,
  FaEnvelope,
  FaDownload,
  FaBriefcase,
  FaGraduationCap,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaRobot,
  FaGlobe,
  FaLink,
  FaBars,
  FaTimes,
  FaChevronDown,
  FaChevronUp,
  FaLinkedin,
  FaPhone
} = Icons;

// Helper to render icons safely
const Icon = ({ Component, ...props }: { Component: any; [key: string]: any }) => {
  return <Component {...props} />;
};

interface Project {
  name: string;
  description: string;
  tech: string[];
  github?: string;
  demo?: string;
  type: 'public' | 'private' | 'freelance';
  preview?: string;
  fullName?: string;
}

function App() {
  const { language, setLanguage, t } = useLanguage();
  const [activeSection, setActiveSection] = useState('hero');
  const [isChatBotOpen, setIsChatBotOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [githubProjects, setGithubProjects] = useState<Project[]>([]);
  const [freelanceProjects, setFreelanceProjects] = useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [isGithubExpanded, setIsGithubExpanded] = useState(false);
  const [isFreelanceExpanded, setIsFreelanceExpanded] = useState(false);
  const currentCvPath = '/Furkan_Gul_CV_2026_TR.pdf';
  const currentCvPreviewImage = '/cv-preview-2026.png';
  const personalityReportPath = '/Kisilik-Analizi-Raporu-3-Donem-Furkan-Gul.pdf';
  const cvPreviewTitle = language === 'tr' ? 'CV Önizleme' : 'CV Preview';
  const cvPreviewHint = language === 'tr' ? 'İndirmek için tıkla' : 'Click to download';

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    element?.scrollIntoView({ behavior: 'smooth' });
    setActiveSection(sectionId);
    setIsMobileMenuOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['hero', 'about', 'experience', 'education', 'projects', 'volunteer', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch GitHub Projects
  useEffect(() => {
    const loadProjects = async () => {
      setLoadingProjects(true);
      try {
        const repos = await fetchGitHubRepos('furkan1287');
        const projects: Project[] = repos.map((repo: GitHubRepo) => ({
          name: repo.name,
          description: repo.description || 'No description available',
          tech: getTechStack(repo),
          github: repo.html_url,
          demo: repo.homepage || undefined,
          type: 'public' as const,
          preview: repo.homepage || undefined,
          fullName: repo.full_name
        }));
        setGithubProjects(projects);
      } catch (error) {
        console.error('Error loading projects:', error);
      } finally {
        setLoadingProjects(false);
      }
    };

    loadProjects();
  }, []);

  // Railway/Freelance Projects
  useEffect(() => {
    const freelance: Project[] = [
      {
        name: 'Pastacı Hamdi',
        description: 'Pastacı Hamdi web sitesi ve yönetim paneli',
        tech: ['React', 'Node.js', 'MongoDB'],
        github: 'https://github.com/furkan1287/pastaci-hamdi',
        demo: 'https://app-production-c402.up.railway.app',
        type: 'freelance' as const,
        preview: 'https://app-production-c402.up.railway.app',
        fullName: 'furkan1287/pastaci-hamdi'
      },
      {
        name: 'PACE - WhatsApp Otomasyon + AI',
        description: 'WhatsApp otomasyon ve AI destekli müşteri yönetim sistemi',
        tech: ['React', 'Node.js', 'AI'],
        github: 'https://github.com/furkan1287/pace',
        demo: 'https://frontend-production-2392.up.railway.app',
        type: 'freelance' as const,
        preview: 'https://frontend-production-2392.up.railway.app',
        fullName: 'furkan1287/pace'
      },
      {
        name: 'SpiderBeats Müzik Platformu',
        description: 'Modern müzik platformu ve streaming servisi',
        tech: ['React', 'Node.js', 'MongoDB'],
        github: 'https://github.com/furkan1287/webspider',
        demo: 'https://webspider-production.up.railway.app',
        type: 'freelance' as const,
        preview: 'https://webspider-production.up.railway.app',
        fullName: 'furkan1287/webspider'
      },
      {
        name: 'Sisore Restaurant',
        description: 'Restoran yönetim ve sipariş sistemi',
        tech: ['React', 'Node.js', 'MongoDB'],
        github: 'https://github.com/furkan1287/sisore',
        demo: 'https://web-production-0caae.up.railway.app',
        type: 'freelance' as const,
        preview: 'https://web-production-0caae.up.railway.app',
        fullName: 'furkan1287/sisore'
      },
      {
        name: 'Bijusaray',
        description: 'E-ticaret sitesi ve admin paneli',
        tech: ['React', 'Node.js', 'MongoDB'],
        github: 'https://github.com/furkan1287/bijusaray',
        demo: 'https://bijusaray.com.tr',
        type: 'freelance' as const,
        preview: 'https://bijusaray.com.tr',
        fullName: 'furkan1287/bijusaray'
      },
      {
        name: 'Payitaht İnşaat',
        description: 'İnşaat şirketi web sitesi ve admin paneli',
        tech: ['React', 'Node.js', 'MongoDB'],
        github: 'https://github.com/furkan1287/payitaht',
        demo: 'https://payitaht-production.up.railway.app',
        type: 'freelance' as const,
        preview: 'https://payitaht-production.up.railway.app',
        fullName: 'furkan1287/payitaht'
      },
      {
        name: 'Ace Up Elo Boost',
        description: 'Profesyonel oyun elo boost hizmeti platformu',
        tech: ['React', 'Node.js', 'MongoDB'],
        github: 'https://github.com/furkan1287/eloboost',
        demo: 'https://eloboost-production.up.railway.app',
        type: 'freelance' as const,
        preview: 'https://eloboost-production.up.railway.app',
        fullName: 'furkan1287/eloboost'
      }
    ];
    setFreelanceProjects(freelance);
  }, []);

  const navItems = [
    { id: 'hero', label: t('nav.home') },
    { id: 'about', label: t('nav.about') },
    { id: 'experience', label: t('nav.experience') },
    { id: 'education', label: t('nav.education') },
    { id: 'projects', label: t('nav.projects') },
    { id: 'volunteer', label: t('nav.volunteer') },
    { id: 'contact', label: t('nav.contact') }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-slate-900/80 backdrop-blur-md z-50 border-b border-indigo-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-4">
              <img
                src="/cv.png"
                alt="Furkan Gül"
                className="w-12 h-12 rounded-full object-cover border-2 border-purple-400"
              />
              <motion.h1
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="text-2xl font-bold bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-400 bg-clip-text text-transparent"
              >
                Furkan Gül
              </motion.h1>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`text-sm font-medium transition-colors duration-200 relative ${
                    activeSection === item.id
                      ? 'text-indigo-400'
                      : 'text-gray-300 hover:text-white'
                  }`}
                >
                  {item.label}
                  {activeSection === item.id && (
                    <motion.div
                      layoutId="activeSection"
                      className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-400"
                    />
                  )}
                </button>
              ))}
              <button
                onClick={() => setLanguage(language === 'tr' ? 'en' : 'tr')}
                className="flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                title={language === 'tr' ? 'Switch to English' : 'Türkçe\'ye Geç'}
              >
                <Icon Component={FaGlobe} className="text-gray-300" />
                <span className="text-sm font-medium text-gray-300">
                  {language === 'tr' ? 'EN' : 'TR'}
                </span>
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-gray-300 hover:text-white"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <Icon Component={FaTimes} size={24} /> : <Icon Component={FaBars} size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden overflow-hidden"
            >
                <div className="py-4 space-y-2">
                  {navItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className={`block w-full text-left px-4 py-2 rounded-lg transition-colors ${
                        activeSection === item.id
                          ? 'bg-indigo-500/20 text-indigo-400'
                          : 'text-gray-300 hover:bg-white/5'
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                  <button
                    onClick={() => setLanguage(language === 'tr' ? 'en' : 'tr')}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-gray-300 hover:bg-white/5 w-full"
                  >
                    <Icon Component={FaGlobe} />
                    <span>{language === 'tr' ? 'EN' : 'TR'}</span>
                  </button>
                </div>
            </motion.div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="hero" className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden pt-20">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(120,119,198,0.3),transparent_50%)]"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-8"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
              className="w-40 h-40 mx-auto mb-8 rounded-full bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500 p-1"
            >
              <img
                src="/cv.png"
                alt="Furkan Gül"
                className="w-full h-full rounded-full object-cover"
              />
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-6xl md:text-7xl font-bold mb-4 bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-400 bg-clip-text text-transparent"
            >
              Furkan Gül
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-2xl md:text-3xl text-gray-300 mb-8"
            >
              {t('hero.subtitle')}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-wrap justify-center gap-4 mb-8"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => scrollToSection('contact')}
                className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 text-white px-8 py-3 rounded-full font-medium hover:shadow-lg hover:shadow-indigo-500/50 transition-all"
              >
                {t('hero.cta.contact')}
              </motion.button>
              <div className="relative group">
                <motion.a
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  href={currentCvPath}
                  download="Furkan_Gul_CV_2026_TR.pdf"
                  className="border-2 border-indigo-400 text-indigo-400 px-8 py-3 rounded-full font-medium hover:bg-indigo-400 hover:text-white transition-all flex items-center gap-2"
                >
                  <Icon Component={FaDownload} /> {t('hero.cta.cv')}
                </motion.a>
                <div className="pointer-events-none absolute left-1/2 top-full z-20 hidden w-[min(90vw,22rem)] -translate-x-1/2 pt-4 md:block">
                  <div className="translate-y-4 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:translate-y-0 group-focus-within:opacity-100">
                    <div className="overflow-hidden rounded-2xl border border-indigo-400/30 bg-slate-950/95 shadow-2xl shadow-indigo-950/60 backdrop-blur-sm">
                      <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
                        <span className="text-sm font-semibold text-white">{cvPreviewTitle}</span>
                        <span className="text-xs text-gray-400">{cvPreviewHint}</span>
                      </div>
                      <img
                        src={currentCvPreviewImage}
                        alt={language === 'tr' ? 'Furkan Gül güncel CV önizlemesi' : 'Preview of Furkan Gül current CV'}
                        className="w-full"
                        loading="lazy"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <motion.a
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href={personalityReportPath}
                target="_blank"
                rel="noopener noreferrer"
                className="border-2 border-purple-400 text-purple-400 px-8 py-3 rounded-full font-medium hover:bg-purple-400 hover:text-white transition-all flex items-center gap-2"
              >
                <Icon Component={FaDownload} /> {t('hero.cta.personality')}
              </motion.a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-black/30 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-400 bg-clip-text text-transparent">
              {t('about.title')}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-400 mx-auto"></div>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="relative self-start"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500 rounded-2xl blur-xl opacity-20"></div>
              <img
                src="/cv.png"
                alt="Furkan Gül"
                className="relative rounded-2xl shadow-2xl w-full max-w-md mx-auto"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <p className="text-lg text-gray-300 leading-relaxed">
                {t('about.description')}
              </p>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg backdrop-blur-sm">
                  <Icon Component={FaMapMarkerAlt} className="text-indigo-400 text-xl" />
                  <span className="text-gray-300">{t('about.location')}</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg backdrop-blur-sm">
                  <Icon Component={FaCalendarAlt} className="text-indigo-400 text-xl" />
                  <span className="text-gray-300">{t('about.role')}</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {[
                  'React', 'React.js', 'ReactJs', 'React Hooks', 'Redux', 'JavaScript', 'TypeScript', 
                  'HTML5', 'CSS', 'CSS3', 'SCSS', 'CSS Preprocessors', 'Component-Based Development', 'DOM', 'Ajax',
                  'C#', '.NET', 'ASP.NET', 'ASP.NET Core', 'Entity Framework', 'Entity Framework Core',
                  'Dapper', 'ADO.NET', 'SQL Server', 'PostgreSQL', 'MongoDB',
                  'RESTful API', 'Microservices', 'Docker', 'Git', 'GitHub',
                  'Java', 'Java OOP', 'Spring', 'Spring Boot', 'JPA', 
                  'Spring Security', 'Spring Web MVC', 'Hibernate',
                  'Dependency Injection', 'SOLID Principles', 'OOP', 
                  'Clean Architecture', 'Layered Architecture', 'Unit Testing',
                  'Integration Testing', 'xUnit', 'Moq', 'Swagger', 'Identity',
                  'Node.js', 'AWS', 'Linux', 'Azure'
                ].map((tech) => (
                  <span
                    key={tech}
                    className="bg-gradient-to-r from-indigo-500/20 via-purple-500/20 to-indigo-500/20 border border-indigo-500/30 text-indigo-300 px-4 py-2 rounded-full text-sm backdrop-blur-sm"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </motion.div>

            {/* Languages Section */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              viewport={{ once: true }}
            >
              <LanguagesSection />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section id="experience" className="py-20 bg-black/20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-400 bg-clip-text text-transparent">
              {t('experience.title')}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-400 mx-auto"></div>
          </motion.div>

          <div className="space-y-4">
            <AccordionCard
              title={t('experience.freelancer.title')}
              subtitle={t('experience.freelancer.year')}
              icon={FaBriefcase}
              delay={0.1}
            >
              <ul className="text-gray-300 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-indigo-400 mt-1">•</span>
                  <span>{t('experience.freelancer.desc1')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-400 mt-1">•</span>
                  <span>{t('experience.freelancer.desc2')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-400 mt-1">•</span>
                  <span>{t('experience.freelancer.desc3')}</span>
                </li>
              </ul>
            </AccordionCard>

            <AccordionCard
              title={t('experience.sistemnetwork.title')}
              subtitle={t('experience.sistemnetwork.year')}
              company={t('experience.sistemnetwork.company')}
              icon={FaBriefcase}
              delay={0.2}
            >
              <ul className="text-gray-300 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-indigo-400 mt-1">•</span>
                  <span>{t('experience.sistemnetwork.desc1')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-400 mt-1">•</span>
                  <span>{t('experience.sistemnetwork.desc2')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-400 mt-1">•</span>
                  <span>{t('experience.sistemnetwork.desc3')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-400 mt-1">•</span>
                  <span>{t('experience.sistemnetwork.desc4')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-400 mt-1">•</span>
                  <span>{t('experience.sistemnetwork.desc5')}</span>
                </li>
              </ul>
            </AccordionCard>

            <AccordionCard
              title={t('experience.techcareer.title')}
              subtitle={t('experience.techcareer.year')}
              company={t('experience.techcareer.company')}
              icon={FaBriefcase}
              delay={0.3}
            >
              <ul className="text-gray-300 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-indigo-400 mt-1">•</span>
                  <span>{t('experience.techcareer.desc1')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-400 mt-1">•</span>
                  <span>{t('experience.techcareer.desc2')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-400 mt-1">•</span>
                  <span>{t('experience.techcareer.desc3')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-400 mt-1">•</span>
                  <span>{t('experience.techcareer.desc4')}</span>
                </li>
              </ul>
            </AccordionCard>

            <AccordionCard
              title={t('experience.fmss.title')}
              subtitle={t('experience.fmss.year')}
              company={t('experience.fmss.company')}
              icon={FaBriefcase}
              delay={0.4}
            >
              <ul className="text-gray-300 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-indigo-400 mt-1">•</span>
                  <span>{t('experience.fmss.desc1')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-400 mt-1">•</span>
                  <span>{t('experience.fmss.desc2')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-400 mt-1">•</span>
                  <span>{t('experience.fmss.desc3')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-400 mt-1">•</span>
                  <span>{t('experience.fmss.desc4')}</span>
                </li>
              </ul>
            </AccordionCard>
          </div>
        </div>
      </section>

      {/* Education Section */}
      <section id="education" className="py-20 bg-black/30 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-400 bg-clip-text text-transparent">
              {t('education.title')}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-400 mx-auto"></div>
          </motion.div>

          <div className="space-y-4">
            <AccordionCard
              title={t('education.university.name')}
              subtitle={t('education.university.degree')}
              icon={FaGraduationCap}
              delay={0.1}
            >
              <div className="space-y-3">
                <p className="text-gray-300">{t('education.university.field')}</p>
                <p className="text-gray-400 text-sm">{t('education.university.period')}</p>
                <a 
                  href="https://www.atu.edu.tr/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-indigo-400 hover:text-indigo-300 text-sm transition-colors inline-block"
                >
                  www.atu.edu.tr →
                </a>
              </div>
            </AccordionCard>

            <AccordionCard
              title={t('education.softito.title')}
              subtitle={t('education.softito.year')}
              icon={FaGraduationCap}
              delay={0.2}
            >
              <div className="space-y-4">
                <p className="text-gray-300 leading-relaxed">
                  {t('education.softito.description')}
                </p>
                <div className="flex flex-wrap gap-2">
                  {['ASP.NET Core', 'Entity Framework', 'SQL Server', 'REST APIs', 'Microservices', 'Docker', 'Git'].map((tech) => (
                    <span
                      key={tech}
                      className="bg-indigo-500/20 border border-indigo-500/30 text-indigo-300 px-4 py-2 rounded-full text-sm"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </AccordionCard>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-20 bg-black/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-400 bg-clip-text text-transparent">
              {t('projects.title')}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-400 mx-auto"></div>
          </motion.div>

          {/* GitHub Projects - Accordion */}
          <div className="mb-12">
            <button
              onClick={() => setIsGithubExpanded(!isGithubExpanded)}
              className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-indigo-500/10 border border-indigo-500/20 rounded-xl hover:border-indigo-500/40 transition-all mb-4"
            >
              <h3 className="text-2xl font-semibold text-white flex items-center gap-3">
                <Icon Component={FaGithub} className="text-indigo-400" />
                GitHub Projeleri
              </h3>
              <Icon 
                Component={isGithubExpanded ? FaChevronUp : FaChevronDown} 
                className="text-indigo-400 text-xl"
              />
            </button>
            {isGithubExpanded && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                {loadingProjects ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-400 mx-auto"></div>
                    <p className="text-gray-400 mt-4">Loading projects...</p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {githubProjects.map((project, index) => (
                      <motion.div
                        key={project.name}
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: index * 0.1 }}
                        viewport={{ once: true }}
                      >
                        <ProjectCard {...project} />
                      </motion.div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </div>

          {/* Freelance Projects - Accordion */}
          {freelanceProjects.length > 0 && (
            <div className="mt-12">
              <button
                onClick={() => setIsFreelanceExpanded(!isFreelanceExpanded)}
                className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-indigo-500/10 border border-indigo-500/20 rounded-xl hover:border-indigo-500/40 transition-all mb-4"
              >
                <h3 className="text-2xl font-semibold text-white flex items-center gap-3">
                  <Icon Component={FaLink} className="text-pink-400" />
                  Freelance İşler / Production Projeleri
                </h3>
                <Icon 
                Component={isFreelanceExpanded ? FaChevronUp : FaChevronDown} 
                className="text-indigo-400 text-xl"
                />
              </button>
              {isFreelanceExpanded && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {freelanceProjects.map((project, index) => (
                      <motion.div
                        key={project.name}
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: index * 0.1 }}
                        viewport={{ once: true }}
                      >
                        <ProjectCard {...project} />
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Volunteer & Social Section */}
      <section id="volunteer" className="py-20 bg-black/30 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-400 bg-clip-text text-transparent">
              {t('volunteer.title')}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-400 mx-auto"></div>
          </motion.div>

          <div className="space-y-4">
            <AccordionCard
              title={t('volunteer.afad.title')}
              subtitle={t('volunteer.afad.year')}
              icon={FaBriefcase}
              delay={0.1}
            >
              <ul className="text-gray-300 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-indigo-400 mt-1">•</span>
                  <span>{t('volunteer.afad.desc1')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-400 mt-1">•</span>
                  <span>{t('volunteer.afad.desc2')}</span>
                </li>
              </ul>
            </AccordionCard>

            <AccordionCard
              title={t('volunteer.aiesec.title')}
              subtitle={t('volunteer.aiesec.year')}
              icon={FaBriefcase}
              delay={0.2}
            >
              <ul className="text-gray-300 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-indigo-400 mt-1">•</span>
                  <span>{t('volunteer.aiesec.desc1')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-400 mt-1">•</span>
                  <span>{t('volunteer.aiesec.desc2')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-400 mt-1">•</span>
                  <span>{t('volunteer.aiesec.desc3')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-400 mt-1">•</span>
                  <span>{t('volunteer.aiesec.desc4')}</span>
                </li>
              </ul>
            </AccordionCard>

            <AccordionCard
              title={t('volunteer.worktravel.title')}
              subtitle={t('volunteer.worktravel.year')}
              icon={FaBriefcase}
              delay={0.3}
            >
              <ul className="text-gray-300 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-indigo-400 mt-1">•</span>
                  <span>{t('volunteer.worktravel.desc1')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-400 mt-1">•</span>
                  <span>{t('volunteer.worktravel.desc2')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-400 mt-1">•</span>
                  <span>{t('volunteer.worktravel.desc3')}</span>
                </li>
              </ul>
            </AccordionCard>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-black/30 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-400 bg-clip-text text-transparent">
              {t('contact.title')}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-400 mx-auto"></div>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Info Card */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-indigo-500/10 border border-indigo-500/20 p-8 rounded-2xl backdrop-blur-sm space-y-6"
            >
              <h3 className="text-2xl font-semibold text-white mb-4">
                {t('contact.subtitle')}
              </h3>
              <p className="text-gray-300">
                {t('contact.description')}
              </p>

              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-white/5 rounded-lg backdrop-blur-sm">
                  <Icon Component={FaEnvelope} className="text-indigo-400 text-xl" />
                  <div>
                    <p className="font-medium text-white">{t('contact.email')}</p>
                    <a href="mailto:furkan@furkangul.net" className="text-gray-300 hover:text-indigo-400 transition-colors">
                      furkan@furkangul.net
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 bg-white/5 rounded-lg backdrop-blur-sm">
                  <Icon Component={FaPhone} className="text-indigo-400 text-xl" />
                  <div>
                    <p className="font-medium text-white">{t('contact.phone')}</p>
                    <a href="tel:+905075847722" className="text-gray-300 hover:text-indigo-400 transition-colors">
                      +90 507 584 77 22
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 bg-white/5 rounded-lg backdrop-blur-sm">
                  <Icon Component={FaGithub} className="text-indigo-400 text-xl" />
                  <div>
                    <p className="font-medium text-white">{t('contact.github')}</p>
                    <a
                      href="https://github.com/furkan1287"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-300 hover:text-indigo-400 transition-colors"
                    >
                      github.com/furkan1287
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 bg-white/5 rounded-lg backdrop-blur-sm">
                  <Icon Component={FaLinkedin} className="text-indigo-400 text-xl" />
                  <div>
                    <p className="font-medium text-white">{t('contact.linkedin')}</p>
                    <a
                      href="https://www.linkedin.com/in/furkan1287/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-300 hover:text-indigo-400 transition-colors"
                    >
                      linkedin.com/in/furkan1287
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-indigo-500/10 border border-indigo-500/20 p-8 rounded-2xl backdrop-blur-sm"
            >
              <h3 className="text-2xl font-semibold text-white mb-6">
                {language === 'tr' ? 'Bana Mesaj Gönderin' : 'Send Me a Message'}
              </h3>
              <ContactForm language={language} />
            </motion.div>
          </div>

          {/* AI Assistant Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            viewport={{ once: true }}
            className="mt-12 bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-indigo-500/10 border border-indigo-500/20 p-8 rounded-2xl backdrop-blur-sm"
          >
            <div className="flex items-center gap-3 mb-4">
              <Icon Component={FaRobot} className="text-indigo-400 text-3xl" />
              <h4 className="text-2xl font-semibold text-white">
                {t('chatbot.title')}
              </h4>
            </div>
            <p className="text-gray-300 mb-6">
              {t('chatbot.description')}
            </p>
            <button
              onClick={() => setIsChatBotOpen(true)}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg font-medium hover:shadow-lg hover:shadow-purple-500/50 transition-all"
            >
              {t('chatbot.button')}
            </button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black/50 py-8 border-t border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-400">
            {t('footer.copyright')}
          </p>
        </div>
      </footer>

      {/* ChatBot - Fixed Bottom Right */}
      <ChatBot
        isOpen={isChatBotOpen}
        onClose={() => setIsChatBotOpen(false)}
        onOpen={() => setIsChatBotOpen(true)}
      />
    </div>
  );
}

export default App;
